import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import API from '@/api';
import { queryKeys } from '@/api/utils/queryKeys';
import { ErrorIds } from '@/constants/ApiResponse';
import { meQueryOptions } from '@/queries/meQuery/meQuery';
import { parseApiError, reportException } from '@/utils/error';
import { assertCondition } from '@/utils/typescript';

type Options = {
  onSuccess: (data: { resendAfter: string; email: string }) => void;
  onAlreadyVerified: () => void;
};

const useRequestEmailVerificationCode = ({ onSuccess, onAlreadyVerified }: Options) => {
  const { t } = useTranslation(['Modal', 'FormErrors']);
  const { data: me } = useQuery(meQueryOptions());
  const queryClient = useQueryClient();
  const requestedEmailRef = useRef('');

  const handleSuccess = ({ resendAfter }: { resendAfter: string }) => {
    onSuccess({ resendAfter, email: requestedEmailRef.current });
  };

  const handleAlreadyVerified = useCallback(
    async (error: unknown) => {
      reportException(
        new Error('Email already verified when requesting verification code', { cause: error }),
        {
          level: 'warning',
        },
      );
      // Await creditActions refresh before onAlreadyVerified: ResumeWithdraw reads the cache via
      // getQueryData and would otherwise reopen the same verification modal on stale reasons.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.me }),
        queryClient.invalidateQueries({ queryKey: queryKeys.creditActions, refetchType: 'all' }),
      ]);
      onAlreadyVerified();
    },
    [onAlreadyVerified, queryClient],
  );

  const {
    mutate: sendCode,
    isPending: isSendPending,
    error: sendCodeError,
    reset: resetSendCode,
  } = useMutation({
    mutationFn: API.user.sendEmailVerificationCode,
    onSuccess: handleSuccess,
    onError: async error => {
      if (parseApiError(error).errorId === ErrorIds.CONFLICT) {
        await handleAlreadyVerified(error);
      }
    },
  });

  const {
    mutate: updateEmail,
    isPending: isUpdatePending,
    error: updateEmailError,
    reset: resetUpdateEmail,
  } = useMutation({
    mutationFn: API.user.updateEmail,
    onSuccess: handleSuccess,
  });

  const reset = useCallback(() => {
    resetSendCode();
    resetUpdateEmail();
  }, [resetSendCode, resetUpdateEmail]);

  const requestEmailVerificationCode = useCallback(
    (email: string) => {
      assertCondition(!!me, 'Me data must exist before requesting email verification code');
      reset();
      requestedEmailRef.current = email;

      if (email.toLocaleLowerCase() === me.email.toLocaleLowerCase()) {
        sendCode();
      } else {
        updateEmail(email);
      }
    },
    [me, reset, sendCode, updateEmail],
  );

  const isSendConflict = !!sendCodeError && parseApiError(sendCodeError).errorId === ErrorIds.CONFLICT;
  const error = isSendConflict ? undefined : (sendCodeError ?? updateEmailError);

  const errorMessage = useMemo(() => {
    if (!error) {
      return undefined;
    }

    return parseApiError(error).errorId === ErrorIds.CONFLICT
      ? t('Modal:EmailVerification.errorDuplicateEmail')
      : t('FormErrors:somethingWentWrong');
  }, [error, t]);

  return {
    requestEmailVerificationCode,
    isPending: isSendPending || isUpdatePending,
    errorMessage,
    reset,
  };
};

export default useRequestEmailVerificationCode;
